import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { DEFAULT_WORK_HOURS, sittingBucketFromWorkHours } from '@erectus/shared';
import type { UserProfile, WorkHours } from '@erectus/shared';
import { Button, Card, ErrorState, Pill, Toggle } from '@/ui/primitives.js';
import { PageHeader } from '@/ui/Layout.js';
import { useAuth } from '@/stores/auth.js';
import { markOnboardingComplete, saveEnrollment, saveProfile, saveSettings } from '@/lib/repo.js';
import { track } from '@/lib/analytics.js';
import { appConfig } from '@/lib/remoteConfig.js';
import { CAPTURE_DEFAULTS } from '@erectus/shared';
import { HeightField } from '@/features/check/HeightField.js';
import { SexField, WeightField } from './BodyFields.js';
import { WorkHoursField } from '@/features/program/WorkHoursField.js';
import { ProgramPicker } from '@/features/program/ProgramPicker.js';

/**
 * Onboarding, three cards (docs/PRODUCT.md §2 screen 3, §12) — kept shallow:
 *   you and your workday (fill in) → goals and desk (tap) → program (read, pick)
 *
 * How to place the phone is not here: it is shown the first time the user
 * opens a session, where it is needed (PhoneGuideIntro).
 *
 * There is no pain, symptom or diagnosis question by design (LEGAL §0); every
 * answer here is ordinary fitness-tracker context. Sex, height and weight are
 * optional and never graded. Work hours bound the daily move reminder.
 */
const STEPS = 3;
const GOALS = [
  { id: 'awareness', label: 'Notice when I slump' },
  { id: 'habit', label: 'Build a check-in habit' },
  { id: 'desk-setup', label: 'Improve my desk setup' },
  { id: 'mobility', label: 'Move more during work' },
  { id: 'track-change', label: 'See change over weeks' },
] as const;

const CHAIRS: Array<{ id: UserProfile['equipment']['chairType']; label: string }> = [
  { id: 'office', label: 'Office chair' },
  { id: 'dining', label: 'Dining chair' },
  { id: 'sofa', label: 'Sofa / bed' },
  { id: 'other', label: 'Something else' },
];

export function OnboardingPage(): ReactNode {
  const { user, userDoc, refreshUserDoc } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [goals, setGoals] = useState<string[]>([]);
  const [sex, setSex] = useState<UserProfile['sex'] | null>(userDoc?.profile?.sex ?? null);
  const [heightCm, setHeightCm] = useState<number | null>(userDoc?.profile?.heightCm ?? null);
  const [weightKg, setWeightKg] = useState<number | null>(userDoc?.profile?.weightKg ?? null);
  const [workHours, setWorkHours] = useState<WorkHours>(userDoc?.settings?.workHours ?? DEFAULT_WORK_HOURS);
  const [programId, setProgramId] = useState<string | null>('upright-foundations-6w');
  const [laptop, setLaptop] = useState(true);
  const [monitor, setMonitor] = useState(false);
  const [chairType, setChairType] = useState<UserProfile['equipment']['chairType']>('office');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const next = (): void => {
    track('onboarding_step', { step: (step + 1) as 2 | 3, completed: false });
    setStep((s) => s + 1);
  };

  const finish = async (): Promise<void> => {
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      await saveProfile(user.uid, {
        displayName: userDoc?.profile?.displayName ?? user.displayName ?? null,
        birthYear: userDoc?.profile?.birthYear ?? new Date().getFullYear() - 30,
        createdAt: userDoc?.profile?.createdAt ?? Date.now(),
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        goals,
        sittingHoursBucket: sittingBucketFromWorkHours(workHours),
        equipment: { laptop, monitor, chairType },
        ...(heightCm ? { heightCm: Math.round(heightCm) } : {}),
        ...(sex ? { sex } : {}),
        ...(weightKg ? { weightKg: Math.round(weightKg * 10) / 10 } : {}),
      });
      await saveSettings(user.uid, {
        reminderEveryDays: appConfig().reminderDefaultDays,
        reminderDow: 1,
        reminderHour: 10,
        alertMode: 'silent',
        sampleFps: CAPTURE_DEFAULTS.sampleFps,
        dimByDefault: false,
        workHours,
        breakNudges: true,
        snackNudges: true,
      });
      if (programId) {
        const midnight = new Date();
        midnight.setHours(0, 0, 0, 0);
        await saveEnrollment(user.uid, { programId, startedAt: midnight.getTime(), endedAt: null });
        track('program_start', { program_id: programId, from: 'onboarding' });
      }
      await markOnboardingComplete(user.uid);
      track('onboarding_step', { step: 4, completed: true });
      await refreshUserDoc();
      navigate(programId ? '/program' : '/progress', { replace: true });
    } catch {
      setError('Could not save your answers. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Set up" subtitle={`Step ${Math.min(step, STEPS)} of ${STEPS}`} />
      <div className="flex gap-1" aria-hidden>
        {Array.from({ length: STEPS }, (_, i) => i + 1).map((s) => (
          <span
            key={s}
            className={`h-1 flex-1 rounded-full ${s <= step ? 'bg-ink' : 'bg-line'}`}
          />
        ))}
      </div>

      {error ? <ErrorState title="Not saved" message={error} /> : null}

      {step === 1 && (
        <Card className="space-y-4">
          <div>
            <h2 className="font-semibold">You and your workday</h2>
            <p className="text-sm text-muted">Body details are optional, used for context, never to grade you.</p>
          </div>
          <SexField value={sex} onChange={setSex} />
          <HeightField heightCm={heightCm} onSave={setHeightCm} />
          <WeightField weightKg={weightKg} onSave={setWeightKg} />
          <div className="border-t border-line" />
          <div className="space-y-2">
            <p className="text-sm font-medium">When do you usually sit at a desk?</p>
            <WorkHoursField value={workHours} onChange={setWorkHours} />
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card className="space-y-4">
          <div className="space-y-2">
            <h2 className="font-semibold">What do you want out of this?</h2>
            <div className="flex flex-wrap gap-2">
              {GOALS.map((goal) => (
                <Pill
                  key={goal.id}
                  active={goals.includes(goal.id)}
                  onClick={() =>
                    setGoals((prev) =>
                      prev.includes(goal.id) ? prev.filter((g) => g !== goal.id) : [...prev, goal.id].slice(0, 5),
                    )
                  }
                >
                  {goal.label}
                </Pill>
              ))}
            </div>
          </div>
          <div className="border-t border-line" />
          <div className="space-y-3">
            <h2 className="font-semibold">Your desk</h2>
            <Toggle checked={laptop} onChange={setLaptop} label="I work on a laptop" />
            <Toggle checked={monitor} onChange={setMonitor} label="I use an external monitor" />
            <div>
              <p className="mb-2 text-sm font-medium">Chair</p>
              <div className="flex flex-wrap gap-2">
                {CHAIRS.map((c) => (
                  <Pill key={c.id} active={chairType === c.id} onClick={() => setChairType(c.id)}>
                    {c.label}
                  </Pill>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <Card className="space-y-1">
            <h2 className="font-semibold">Pick a program</h2>
            <p className="text-sm text-muted">
              A 15-minute camera check each week, breaks through your workday and short exercise snacks, week by
              week. You can change it later.
            </p>
          </Card>
          <ProgramPicker value={programId} onChange={setProgramId} />
        </div>
      )}

      <div className="flex gap-2">
        {step > 1 ? (
          <Button variant="secondary" onClick={() => setStep((s) => s - 1)}>
            Back
          </Button>
        ) : null}
        {step < STEPS ? (
          <Button className="flex-1" onClick={next} disabled={step === 2 && goals.length === 0}>
            Continue
          </Button>
        ) : (
          <Button className="flex-1" onClick={() => void finish()} disabled={busy}>
            {busy ? 'Saving…' : 'Done'}
          </Button>
        )}
      </div>
    </div>
  );
}
